import React, { Fragment, useState, useEffect } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { XIcon } from "@heroicons/react/outline";
import CustomerServices from "../../../services/CustomerServices";
import fire from "../../../utils/Toastr";
import { HollowDotsSpinner } from "react-epic-spinners";

export default function ApproveAccountOpeningModal({ isOpen, onClose, approvalId, onSuccess }) {
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [details, setDetails] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [action, setAction] = useState("Approve");
  const [comment, setComment] = useState("");
  const [commentError, setCommentError] = useState("");

  useEffect(() => {
    if (isOpen && approvalId) {
      fetchDetails();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, approvalId]);

  const fetchDetails = async () => {
    setLoadingDetails(true);
    setDetails(null);
    setAction("Approve");
    setComment("");
    setCommentError("");
    try {
      const response = await CustomerServices.viewAccountOpeningApproval(approvalId);
      if (response && response.data) {
        setDetails(response.data);
      } else {
        fire("error", response?.message || "Failed to load approval details");
      }
    } catch (error) {
      console.error("Error fetching account opening approval:", error);
      fire("error", "Failed to load approval details");
    } finally {
      setLoadingDetails(false);
    }
  };

  const handleSubmit = async () => {
    if (!details) return;

    if (action === "Reject" && comment.trim().length === 0) {
      setCommentError("Rejection reason is required");
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        approvalId: details.id,
        approveOrReject: action === "Approve",
        rejectionReason: action === "Reject" ? comment.trim() : "",
      };

      const response = await CustomerServices.approveOrRejectAccountOpening(payload);

      if (response && response.status !== false) {
        fire("success", response?.message || `Approval ${action.toLowerCase()}d successfully`);
        onSuccess();
        onClose();
      } else {
        fire("error", response?.message || `Failed to ${action.toLowerCase()} approval`);
      }
    } catch (error) {
      console.error("Error processing approval:", error);
      fire("error", `An error occurred while processing the approval`);
    } finally {
      setSubmitting(false);
    }
  };

  const canAct = details?.status === "Initiated";

  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-2xl sm:p-6">
                <div className="absolute right-0 top-0 hidden pr-4 pt-4 sm:block">
                  <button
                    type="button"
                    className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none"
                    onClick={onClose}
                  >
                    <span className="sr-only">Close</span>
                    <XIcon className="h-6 w-6" aria-hidden="true" />
                  </button>
                </div>

                <div className="sm:flex sm:items-start">
                  <div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
                    <Dialog.Title as="h3" className="text-lg font-semibold leading-6 text-gray-900 mb-4">
                      {canAct ? "Review Account Opening Request" : "Account Opening Approval Details"}
                    </Dialog.Title>

                    {loadingDetails ? (
                      <div className="flex justify-center py-8">
                        <HollowDotsSpinner size={30} color="#4D627E" />
                      </div>
                    ) : !details ? (
                      <p className="text-sm text-gray-500">Approval details not available.</p>
                    ) : (
                      <div className="space-y-4">
                        {/* Status Banner */}
                        <div className={`border-l-4 p-4 ${
                          details.status === "Initiated"
                            ? "bg-yellow-50 border-yellow-400"
                            : details.status === "Approved"
                            ? "bg-green-50 border-green-400"
                            : "bg-red-50 border-red-400"
                        }`}>
                          <div className="flex">
                            <div className="ml-3">
                              <p className={`text-sm ${
                                details.status === "Initiated"
                                  ? "text-yellow-700"
                                  : details.status === "Approved"
                                  ? "text-green-700"
                                  : "text-red-700"
                              }`}>
                                <span className="font-medium">Status: {details.status}</span>
                              </p>
                              <p className="text-sm text-gray-600 mt-1">
                                Initiated By: <span className="font-medium">{details.initiatedBy || "-"}</span>
                              </p>
                              <p className="text-sm text-gray-600">
                                Date Initiated: <span className="font-medium">{details.dateInitiated ? new Date(details.dateInitiated).toLocaleString() : "-"}</span>
                              </p>
                              {details.approvedBy && (
                                <p className="text-sm text-gray-600">
                                  Approved By: <span className="font-medium">{details.approvedBy}</span>
                                </p>
                              )}
                              {details.rejectedBy && (
                                <p className="text-sm text-gray-600">
                                  Rejected By: <span className="font-medium">{details.rejectedBy}</span>
                                </p>
                              )}
                              {details.rejectionReason && (
                                <p className="text-sm text-gray-600 mt-2">
                                  Rejection Reason: <span className="font-medium">{details.rejectionReason}</span>
                                </p>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Request Details */}
                        <div className="border rounded-lg p-4 bg-gray-50">
                          <h4 className="font-semibold text-gray-700 mb-2">Request Details</h4>
                          <div className="space-y-2">
                            <p><span className="text-sm font-medium">User ID:</span> {details.requestData?.userId ?? "-"}</p>
                            <p><span className="text-sm font-medium">User Name:</span> {details.requestData?.userName || "N/A"}</p>
                            <p><span className="text-sm font-medium">Unique Identifier (BVN / NIN):</span> {details.requestData?.uniqueIdentifier || "N/A"}</p>
                            {details.reason && (
                              <p><span className="text-sm font-medium">Reason:</span> {details.reason}</p>
                            )}
                          </div>
                        </div>

                        {/* Action Buttons - Only show for initiated requests */}
                        {canAct && (
                          <>
                            <div className="mt-4">
                              <label className="block text-sm font-medium text-gray-700">Action</label>
                              <select
                                value={action}
                                onChange={(e) => setAction(e.target.value)}
                                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                              >
                                <option value="Approve">Approve</option>
                                <option value="Reject">Reject</option>
                              </select>
                            </div>

                            {action === "Reject" && (
                              <div className="mt-4">
                                <label className="block text-sm font-medium text-gray-700">Rejection Reason (Required)</label>
                                <textarea
                                  value={comment}
                                  onChange={(e) => {
                                    setComment(e.target.value);
                                    if (e.target.value.trim().length > 0) {
                                      setCommentError("");
                                    }
                                  }}
                                  rows={3}
                                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                                  placeholder="Enter rejection reason..."
                                />
                                {commentError && (
                                  <p className="mt-1 text-sm text-red-600">{commentError}</p>
                                )}
                              </div>
                            )}

                            <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                              {submitting ? (
                                <div className="flex justify-center w-full">
                                  <HollowDotsSpinner size={30} color="#4D627E" />
                                </div>
                              ) : (
                                <>
                                  <button
                                    type="button"
                                    onClick={handleSubmit}
                                    className={`inline-flex w-full justify-center rounded-md px-3 py-2 text-sm font-semibold text-white shadow-sm sm:ml-3 sm:w-auto ${
                                      action === "Approve"
                                        ? "bg-green-600 hover:bg-green-500"
                                        : "bg-red-600 hover:bg-red-500"
                                    }`}
                                  >
                                    {action}
                                  </button>
                                  <button
                                    type="button"
                                    className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
                                    onClick={onClose}
                                  >
                                    Close
                                  </button>
                                </>
                              )}
                            </div>
                          </>
                        )}

                        {/* Close button for non-initiated requests */}
                        {!canAct && (
                          <div className="mt-5 sm:mt-4 sm:flex sm:justify-end">
                            <button
                              type="button"
                              className="inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:w-auto"
                              onClick={onClose}
                            >
                              Close
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
